import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
    ChartBar, ChartLine, ChartPieSlice, Clock, Gauge, Train, Wrench,
    FloppyDisk, ArrowClockwise, FilePdf, FileCsv, Waveform, Trash,
} from "@phosphor-icons/react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    ReferenceLine, BarChart, Bar, Cell, PieChart, Pie, Legend,
} from "recharts";
import TipplerRailBridge from "./TipplerRailBridge";

/* ═══════════════════════════════════════════════════════════════════════
 * CoalTipplerDashboard — GTRB coal tippler live dashboard.
 *   • KPIs (Availability, Target, Cycle, Delay, Maintenance, Wagons)
 *   • Time-breakdown horizontal stack + Delay donut + Delay-by-category bar
 *   • Target-performance donut + Recent records table + Trend line
 *   • Data-entry form for Tippler operators / Planners / Admin
 *   • CSV/PDF export + full SigmaXL SPC popup
 * ═══════════════════════════════════════════════════════════════════════ */

const CT_INPUT_ROLES = ["admin", "manager", "tippler_operator"];

/** Equipment picklist for the "Maintenance delay caused by" field. Keep
 *  this list in sync with the coal-tippler equipment on site. */
const EQUIPMENT_OPTIONS = [
    "Main Tippler",
    "Charger car",
    "Tippler PLC",
    "NCV01 conveyor",
    "NCV01 conveyor scale",
    "Apron feeder 1",
    "Apron feeder 2",
    "Apron feeder 3",
    "Apron feeder 4",
    "NCV02 conveyor",
    "NCV02 conveyor moving head",
    "NCV03 conveyor",
    "NCV05 conveyor",
];

function hms(seconds) {
    if (seconds == null || Number.isNaN(seconds)) return "—";
    const s = Math.max(0, Math.round(Number(seconds)));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}
function hm(seconds) { return hms(seconds).slice(0, 5); }
function pct(v, digits = 1) {
    if (v == null || Number.isNaN(v)) return "—";
    return `${Number(v).toFixed(digits)}%`;
}
function todayISO() { return new Date().toISOString().slice(0, 10); }

function KpiCard({ title, value, subtitle, tone = "navy", icon }) {
    const toneMap = {
        green: "bg-emerald-600 text-white",
        amber: "bg-amber-500 text-slate-900",
        blue:  "bg-blue-700 text-white",
        red:   "bg-red-600 text-white",
        navy:  "bg-[#0F172A] text-white",
        slate: "bg-slate-800 text-white",
    };
    return (
        <div className={`rounded-lg p-4 shadow-sm ${toneMap[tone]}`} data-testid={`ct-kpi-${title.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-90">{title}</div>
                {icon}
            </div>
            <div className="text-3xl font-black leading-tight mt-1">{value}</div>
            {subtitle && <div className="text-[11px] opacity-90 mt-1">{subtitle}</div>}
        </div>
    );
}

function HeadedCard({ title, right, children, testid }) {
    return (
        <div className="g-card" data-testid={testid}>
            <div className="g-card-header-dark flex items-center justify-between">
                <div className="font-display font-bold uppercase tracking-wide text-sm">{title}</div>
                {right}
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

function TimeBreakdownBar({ segments }) {
    const total = segments.reduce((a, s) => a + Math.max(0, s.seconds || 0), 0) || 1;
    return (
        <div className="w-full">
            <div className="text-center font-bold text-lg mb-1" data-testid="ct-time-breakdown-total">
                {hms(total)}
            </div>
            <div className="flex h-10 rounded overflow-hidden shadow-inner ring-1 ring-slate-200">
                {segments.map((s) => {
                    const w = ((Math.max(0, s.seconds || 0) / total) * 100).toFixed(2);
                    if (Number(w) === 0) return null;
                    return (
                        <div key={s.key || s.label} className="flex items-center justify-center text-white text-[11px] font-bold"
                             style={{ width: `${w}%`, background: s.color }} title={`${s.label}: ${hms(s.seconds)}`}>
                            {Number(w) >= 7 ? hms(s.seconds) : ""}
                        </div>
                    );
                })}
            </div>
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-700">
                {segments.map((s) => (
                    <div key={s.key || s.label} className="flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: s.color }} />
                        {s.label}
                    </div>
                ))}
            </div>
        </div>
    );
}

function DonutChart({ data, centerText, testid }) {
    return (
        <div className="relative w-full h-56" data-testid={testid}>
            <ResponsiveContainer>
                <PieChart>
                    <Pie data={data} dataKey="value" nameKey="label" innerRadius={55}
                         outerRadius={85} startAngle={90} endAngle={-270} paddingAngle={1}>
                        {data.map((d) => <Cell key={d.label} fill={d.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${Number(v).toFixed(1)}%`, n]} />
                </PieChart>
            </ResponsiveContainer>
            {centerText && (
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="text-2xl font-black">{centerText}</div>
                </div>
            )}
        </div>
    );
}

function HorizontalBar({ rows, testid }) {
    return (
        <div className="space-y-2" data-testid={testid}>
            {rows.map((r) => (
                <div key={r.label}>
                    <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-700 font-medium">{r.label}</span>
                        <span className="font-bold text-slate-800">{pct(r.value)}</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full"
                             style={{ width: `${Math.min(100, r.value)}%`, background: r.color }} />
                    </div>
                </div>
            ))}
        </div>
    );
}

const DEFAULT_FORM = {
    date: todayISO(),
    train_id: "",
    shift: "day",
    wagon_count: "",
    offloading_start: "",
    offloading_end: "",
    maintenance_activation_time: "",
    maintenance_arrival_time: "",
    repair_start_time: "",
    repair_end_time: "",
    equipment: "",
    shunting_minutes: "",
    placement_minutes: "",
    removal_minutes: "",
    other_operational_minutes: "",
    remarks: "",
    // Retroactive-only: extra delays operators want to backfill
    additional_delays: [],
};

const EMPTY_EXTRA_DELAY = {
    types: [],
    reason: "",
    equipment: "",
    start_time: "",
    end_time: "",
    description: "",
};

export default function CoalTipplerDashboard({ user }) {
    const canInput = user && CT_INPUT_ROLES.includes(user.role);
    const canDelete = user && user.role === "admin";
    const isTipplerRole = user && (user.role === "tippler_operator" || user.role === "admin" || user.role === "manager");

    const [date, setDate] = useState(todayISO());
    const [shift, setShift] = useState("");    // "" = all shifts
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showSpc, setShowSpc] = useState(false);
    const [showExport, setShowExport] = useState(false);
    const [form, setForm] = useState(DEFAULT_FORM);
    // ── Live operation state ─────────────────────────────────────────────
    const [live, setLive] = useState({ consignment: null, delays: [] });
    const [startForm, setStartForm] = useState({
        train_id: "", wagon_count: "", shift: "day", remarks: "",
    });
    const [showStart, setShowStart] = useState(false);
    const [showDelay, setShowDelay] = useState(false);
    const [feedbackDelay, setFeedbackDelay] = useState(null);   // when set → open millwright modal
    const [delayForm, setDelayForm] = useState({
        types: [], reason: "", equipment: "", description: "", eta_minutes: "",
        shunting_minutes: "", placement_minutes: "",
        removal_minutes: "", other_operational_minutes: "",
    });

    const loadLive = useCallback(async () => {
        try {
            const r = await api.get("/coal-tippler/consignments/active");
            setLive(r.data || { consignment: null, delays: [] });
        } catch (e) { console.warn("live load failed:", formatApiError(e)); }
    }, []);
    useEffect(() => { loadLive(); }, [loadLive]);
    // Poll live state every 15 s so multiple viewers stay in sync
    useEffect(() => {
        const t = setInterval(loadLive, 15000);
        return () => clearInterval(t);
    }, [loadLive]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { date };
            if (shift) params.shift = shift;
            const r = await api.get("/coal-tippler/dashboard", { params });
            setData(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [date, shift]);

    useEffect(() => { load(); }, [load]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    /** Stamp the current wall-clock time (HH:MM:SS) into a form field. */
    const stampNow = (k) => () => {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        const now = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        setForm((f) => ({ ...f, [k]: now }));
    };

    // ── Live operation handlers ─────────────────────────────────────────
    /** Start a live tippler operation from a rail consignment (bridge).
     *  Auto-accepts any remaining released wagons on the rail side, then
     *  creates the linked coal-tippler consignment. This is the only way
     *  to start an operation now that the standalone "Start" button is
     *  gone — every tippler op must be tied to a rail consignment. */
    const startFromRail = async (rc) => {
        try {
            const remainingToAccept = Math.max(0,
                (rc.wagons_released || 0) - (rc.wagons_accepted_at_tippler || 0));
            if (remainingToAccept > 0) {
                await api.post(`/rail/consignments/${rc.id}/action`, {
                    action: "accept_at_tippler",
                    count: remainingToAccept,
                });
            }
            const acceptedTotal = (rc.wagons_accepted_at_tippler || 0) + remainingToAccept;
            // Batch wagon count = wagons still to tip at the tippler right now
            // (not cumulative). This keeps tippler.current_wagon / wagon_count
            // meaningful across multi-batch consignments. Fallback to 1 to
            // satisfy the backend's min-1 constraint.
            const batchWagonCount = Math.max(1, acceptedTotal - (rc.wagons_tipped || 0));
            await api.post("/coal-tippler/consignments", {
                date: date || todayISO(),
                train_id: rc.train_id || rc.consignment_no,
                shift: startForm.shift || "day",
                wagon_count: batchWagonCount,
                remarks: `Rail consignment ${rc.consignment_no}${rc.mine_name ? ` · ${rc.mine_name}` : ""}${rc.coal_grade ? ` · ${rc.coal_grade}` : ""} · batch of ${batchWagonCount} · ${rc.wagons_tipped || 0}/${rc.wagon_count} tipped so far`,
                rail_consignment_id: rc.id,
            });
            toast.success(`Started tippler operation for ${rc.consignment_no}`);
            await loadLive();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const startConsignment = async () => {
        if (!startForm.train_id.trim() || !startForm.wagon_count) {
            toast.error("Train ID and Wagon Count are required");
            return;
        }
        try {
            await api.post("/coal-tippler/consignments", {
                date: date || todayISO(),
                train_id: startForm.train_id.trim(),
                shift: startForm.shift,
                wagon_count: Number(startForm.wagon_count),
                remarks: startForm.remarks || null,
            });
            toast.success("Consignment started — begin tippling");
            setStartForm({ train_id: "", wagon_count: "", shift: "day", remarks: "" });
            setShowStart(false);
            await loadLive();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const bumpWagon = async (delta) => {
        if (!live.consignment) return;
        const next = Math.max(0, Math.min(
            live.consignment.wagon_count,
            (live.consignment.current_wagon || 0) + delta,
        ));
        try {
            await api.patch(`/coal-tippler/consignments/${live.consignment.id}/wagon`,
                            { current_wagon: next });
            // Mirror to the linked rail consignment so wagons_tipped stays in
            // sync end-to-end. Only positive deltas count on the rail side.
            const railId = live.consignment.rail_consignment_id;
            if (railId && delta > 0) {
                try {
                    await api.post(`/rail/consignments/${railId}/action`, {
                        action: "record_tipped", count: delta,
                    });
                } catch (re) {
                    // Log but don't fail the wagon bump — the tippler side is
                    // source of truth; the coordinator can reconcile if needed.
                    console.warn("rail record_tipped mirror failed:", formatApiError(re));
                }
            }
            await loadLive();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleDelayType = (t) => setDelayForm((f) => ({
        ...f,
        types: f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t],
    }));

    const logDelay = async () => {
        if (!live.consignment) return;
        if (!delayForm.types.length) {
            toast.error("Pick at least one delay type"); return;
        }
        if (!delayForm.reason.trim()) {
            toast.error("A reason is required"); return;
        }
        if (delayForm.types.includes("breakdown") && !delayForm.equipment) {
            toast.error("Pick the equipment for the breakdown"); return;
        }
        try {
            await api.post(`/coal-tippler/consignments/${live.consignment.id}/delays`, {
                types: delayForm.types,
                reason: delayForm.reason.trim(),
                equipment: delayForm.equipment || null,
                description: delayForm.description || null,
                eta_minutes: delayForm.eta_minutes ? Number(delayForm.eta_minutes) : null,
                shunting_seconds: Math.round(Number(delayForm.shunting_minutes || 0) * 60),
                placement_seconds: Math.round(Number(delayForm.placement_minutes || 0) * 60),
                removal_seconds: Math.round(Number(delayForm.removal_minutes || 0) * 60),
                other_operational_seconds: Math.round(Number(delayForm.other_operational_minutes || 0) * 60),
            });
            toast.success(delayForm.types.includes("breakdown")
                ? "Breakdown logged — millwright activated"
                : "Delay logged");
            setDelayForm({
                types: [], reason: "", equipment: "", description: "", eta_minutes: "",
                shunting_minutes: "", placement_minutes: "",
                removal_minutes: "", other_operational_minutes: "",
            });
            setShowDelay(false);
            await loadLive();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const resolveDelay = async (delayId, delayLabel) => {
        if (!window.confirm(`Resolve delay "${delayLabel}" now?`)) return;
        try {
            await api.patch(`/coal-tippler/delays/${delayId}/resolve`, {});
            toast.success("Delay resolved · operation resumed");
            await loadLive();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    /** Millwright acknowledges a coal-tippler breakdown from within the
     *  operator dashboard. Delegates to the shared millwright endpoint. */
    const ackBreakdown = async (delay) => {
        if (!delay?.millwright?.notification_id) return;
        try {
            await api.post(`/millwright/breakdown-notifications/${delay.millwright.notification_id}/acknowledge`, {
                note: "Attending — en route.",
            });
            toast.success("Breakdown acknowledged");
            await loadLive();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    /** Millwright / operator posts feedback on the breakdown. Uses shared
     *  endpoint so vessel + coal-tippler timelines look identical. */
    const postFeedback = async (notificationId, text, kind = "update") => {
        if (!notificationId || !text?.trim()) return;
        try {
            await api.post(`/millwright/breakdown-notifications/${notificationId}/feedback`, {
                text: text.trim(),
                kind,
            });
            toast.success("Feedback posted");
            await loadLive();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const endOperation = async () => {
        if (!live.consignment) return;
        const c = live.consignment;
        if (c.current_wagon < c.wagon_count) {
            toast.error(`Cannot end: ${c.current_wagon} of ${c.wagon_count} wagons in this batch tippled.`);
            return;
        }
        // If linked to a rail consignment, check whether the whole rail
        // consignment is done. If not, this is just a BATCH end — the
        // coordinator may still release more wagons for the next batch.
        let railTotal = 0, railTipped = 0, railStillPending = 0;
        if (c.rail_consignment_id) {
            try {
                const r = await api.get(`/rail/consignments/${c.rail_consignment_id}`);
                const rc = r.data?.consignment || {};
                railTotal = Number(rc.wagon_count || 0);
                railTipped = Number(rc.wagons_tipped || 0);
                railStillPending = Math.max(0, railTotal - railTipped);
            } catch { /* fall through */ }
        }
        const consignmentDone = c.rail_consignment_id
            ? railStillPending === 0
            : true;
        const label = consignmentDone
            ? `End consignment ${c.train_id}? All wagons tippled — this will finalise it.`
            : `End batch for ${c.train_id}? ${railTipped}/${railTotal} tipped on the rail consignment · ${railStillPending} wagons still pending. Ask the coordinator to release more when ready.`;
        if (!window.confirm(label)) return;
        try {
            await api.post(`/coal-tippler/consignments/${c.id}/end`, {});
            // Only auto-fire rail complete_consignment when EVERY rail wagon
            // has been tipped — mid-cycle batch ends leave the rail
            // consignment open so the coordinator can release more.
            if (c.rail_consignment_id && consignmentDone) {
                try {
                    await api.post(`/rail/consignments/${c.rail_consignment_id}/action`, {
                        action: "complete_consignment",
                    });
                } catch (re) {
                    console.warn("rail complete_consignment mirror failed:", formatApiError(re));
                }
            }
            toast.success(consignmentDone
                ? "Consignment completed · rail closed · empties ready to release"
                : `Batch of ${c.wagon_count} wagons ended · ${railStillPending} still pending · ask coordinator to release more`);
            await loadLive();
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    /** Fill the entire form with a plausible sample record. Handy for testing
     *  the dashboard when no live operation is running. Randomises the numbers
     *  so multiple presses generate a spread of records for the analytics. */
    const fillSample = () => {
        const pad = (n) => String(n).padStart(2, "0");
        // Random offload window between 3.2 h and 5.5 h so both target-met and
        // target-missed cases appear across a batch of test records.
        const startHour = 6 + Math.floor(Math.random() * 8);        // 06:00–13:00
        const startMin  = Math.floor(Math.random() * 60);
        const durMin    = 190 + Math.floor(Math.random() * 140);    // 3h10 – 5h30
        const start = new Date(2020, 0, 1, startHour, startMin);
        const end   = new Date(start.getTime() + durMin * 60 * 1000);
        const fmt = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

        // Half the sample records include a maintenance event.
        const hasMaint = Math.random() < 0.7;
        let maintAct = "", maintArr = "", repStart = "", repEnd = "", equipment = "";
        if (hasMaint) {
            const actOffset  = 20 + Math.floor(Math.random() * 40);
            const arrOffset  = actOffset + 10 + Math.floor(Math.random() * 25);
            const repOffset  = arrOffset + 5 + Math.floor(Math.random() * 15);
            const repDur     = 20 + Math.floor(Math.random() * 90);
            const actAt = new Date(start.getTime() + actOffset * 60000);
            const arrAt = new Date(start.getTime() + arrOffset * 60000);
            const repAt = new Date(start.getTime() + repOffset * 60000);
            const repEndAt = new Date(repAt.getTime() + repDur * 60000);
            maintAct = fmt(actAt); maintArr = fmt(arrAt);
            repStart = fmt(repAt); repEnd = fmt(repEndAt);
            equipment = EQUIPMENT_OPTIONS[Math.floor(Math.random() * EQUIPMENT_OPTIONS.length)];
        }

        // Realistic operational-delay minutes.
        const rndMin = (lo, hi) => (lo + Math.random() * (hi - lo)).toFixed(1);

        // Train ID: T + 3-digit random not clashing with existing records if we can help it.
        const rnd = String(100 + Math.floor(Math.random() * 900));

        setForm({
            date: date || todayISO(),
            train_id: `T${rnd}`,
            shift: form.shift || "day",
            wagon_count: String(28 + Math.floor(Math.random() * 12)),
            offloading_start: fmt(start),
            offloading_end: fmt(end),
            maintenance_activation_time: maintAct,
            maintenance_arrival_time: maintArr,
            repair_start_time: repStart,
            repair_end_time: repEnd,
            equipment,
            shunting_minutes: rndMin(5, 15),
            placement_minutes: rndMin(4, 12),
            removal_minutes: rndMin(3, 10),
            other_operational_minutes: rndMin(2, 8),
            remarks: hasMaint
                ? `Sample record · maintenance on ${equipment}`
                : "Sample record · no maintenance event",
        });
        toast.success("Sample data filled — review and click Save");
    };

    const submit = async () => {
        if (!form.date || !form.train_id.trim() || !form.wagon_count ||
            !form.offloading_start || !form.offloading_end) {
            toast.error("Date, Train ID, Wagons, Offload Start and Offload End are required");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                date: form.date,
                train_id: form.train_id.trim(),
                shift: form.shift || null,
                wagon_count: Number(form.wagon_count),
                offloading_start: form.offloading_start,
                offloading_end: form.offloading_end,
                maintenance_activation_time: form.maintenance_activation_time || null,
                maintenance_arrival_time: form.maintenance_arrival_time || null,
                repair_start_time: form.repair_start_time || null,
                repair_end_time: form.repair_end_time || null,
                equipment: form.equipment || null,
                shunting_seconds: Math.round(Number(form.shunting_minutes || 0) * 60),
                placement_seconds: Math.round(Number(form.placement_minutes || 0) * 60),
                removal_seconds: Math.round(Number(form.removal_minutes || 0) * 60),
                other_operational_seconds: Math.round(Number(form.other_operational_minutes || 0) * 60),
                remarks: form.remarks || null,
                additional_delays: (form.additional_delays || [])
                    .filter((d) => d.types.length && d.reason && d.start_time && d.end_time)
                    .map((d) => ({
                        types: d.types,
                        reason: d.reason,
                        equipment: d.equipment || null,
                        start_time: d.start_time,
                        end_time: d.end_time,
                        description: d.description || null,
                    })),
            };
            await api.post("/coal-tippler/records", payload);
            toast.success("Record saved · all calculations updated");
            setForm({ ...DEFAULT_FORM, date, shift: form.shift });
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const clear = () => setForm({ ...DEFAULT_FORM, date, shift: form.shift });

    const remove = async (id, trainId) => {
        if (!window.confirm(`Delete record ${trainId}? This cannot be undone.`)) return;
        try {
            await api.delete(`/coal-tippler/records/${id}`);
            toast.success("Record deleted");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    /** Admin-only: ask the backend to seed N plausible records across the
     *  past N days so the dashboard + SPC has enough signal for demos. */
    const seedSamples = async () => {
        if (!window.confirm("Seed 10 sample records across the past 7 days? Existing records won't be touched.")) return;
        try {
            const r = await api.post("/coal-tippler/seed-samples", null, { params: { count: 10, days_back: 7 } });
            toast.success(`Seeded ${r.data.created} sample records`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    /** Admin-only: wipe every record tagged as sample data. */
    const wipeSamples = async () => {
        if (!window.confirm("Delete every sample record (any record with 'Sample record' in its remark)? This cannot be undone.")) return;
        try {
            const r = await api.delete("/coal-tippler/seed-samples");
            toast.success(`Deleted ${r.data.deleted} sample records`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const doExport = (fmt) => {
        const params = new URLSearchParams();
        if (date) params.set("date", date);
        if (shift) params.set("shift", shift);
        const url = `${api.defaults.baseURL}/coal-tippler/export/${fmt}?${params.toString()}`;
        window.open(url, "_blank", "noopener");
        setShowExport(false);
    };

    // --- Derived data for the charts ---
    const kpi = data?.kpis || {};
    const breakdown = data?.delay_breakdown || {};
    const opDelay = data?.operational_delay || {};

    const donutDelay = useMemo(() => ([
        { label: "Waiting for Maintenance", value: breakdown.waiting_for_maintenance_pct || 0, color: "#DC2626" },
        { label: "Maintenance Duration",    value: breakdown.maintenance_duration_pct || 0,    color: "#F59E0B" },
        { label: "Operational Delay",       value: breakdown.operational_delay_pct || 0,       color: "#2563EB" },
        { label: "Other / Idle",            value: breakdown.other_pct || 0,                   color: "#059669" },
    ]), [breakdown]);

    const barDelay = donutDelay.map((d) => ({ label: d.label, value: d.value, color: d.color }));

    const donutTarget = useMemo(() => ([
        { label: "Met Target (≤ 4 h)", value: kpi.target_met_pct || 0, color: "#059669" },
        { label: "Missed Target (> 4 h)", value: Math.max(0, 100 - (kpi.target_met_pct || 0)), color: "#DC2626" },
    ]), [kpi]);

    const trend = data?.trend || [];
    const trendChart = trend.map((t, i) => ({
        idx: i + 1,
        label: t.train_id || String(i + 1),
        availability: Number(t.availability_pct || 0),
        cycle_min: Math.round((t.cycle_time_seconds || 0) / 60),
    }));

    return (
        <div className="space-y-4" data-testid="coal-tippler-dashboard">
            {/* Rail Coordinator → Coal Tippler bridge (connected consignment flow) */}
            <TipplerRailBridge user={user} live={live} onStartOperation={startFromRail} />
            {/* Header band */}
            <div className="g-card overflow-hidden">
                <div className="flex flex-col md:flex-row md:items-center gap-3 p-4 bg-gradient-to-r from-[#0B1A3B] via-[#0F1E3D] to-[#0B1A3B] text-white">
                    <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg bg-black/40 flex items-center justify-center">
                            <Train size={30} weight="fill" className="text-amber-300" />
                        </div>
                        <div>
                            <div className="text-2xl font-black leading-tight">COAL TIPPLER DASHBOARD</div>
                            <div className="text-amber-300 text-xs font-semibold tracking-wider">
                                TRACK DELAYS &nbsp;|&nbsp; IDENTIFY LOSS &nbsp;|&nbsp; IMPROVE AVAILABILITY
                            </div>
                        </div>
                    </div>
                    <div className="md:ml-auto flex flex-wrap items-end gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-white/70">Date</label>
                            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                                   className="block bg-white/95 text-slate-900 rounded px-2 py-1 text-sm"
                                   data-testid="ct-date-filter" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-white/70">Shift</label>
                            <select value={shift} onChange={(e) => setShift(e.target.value)}
                                    className="block bg-white/95 text-slate-900 rounded px-2 py-1 text-sm"
                                    data-testid="ct-shift-filter">
                                <option value="">All shifts</option>
                                <option value="day">Day Shift</option>
                                <option value="night">Night Shift</option>
                            </select>
                        </div>
                        <button onClick={load} className="px-3 py-1.5 rounded bg-white/15 hover:bg-white/25 text-white text-xs font-bold flex items-center gap-1"
                                data-testid="ct-refresh-btn">
                            <ArrowClockwise size={14} weight="bold" /> {loading ? "Loading…" : "Refresh"}
                        </button>
                        <button onClick={() => setShowSpc(true)} className="px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs font-bold flex items-center gap-1"
                                data-testid="ct-spc-btn">
                            <Waveform size={14} weight="bold" /> Full Six-Sigma Analysis
                        </button>
                        <div className="relative">
                            <button onClick={() => setShowExport((v) => !v)}
                                    className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1"
                                    data-testid="ct-export-btn">
                                Export ▾
                            </button>
                            {showExport && (
                                <div className="absolute right-0 mt-1 bg-white shadow-lg rounded border border-slate-200 py-1 z-10 min-w-[180px]">
                                    <button onClick={() => doExport("pdf")}
                                            className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2 text-slate-800"
                                            data-testid="ct-export-pdf">
                                        <FilePdf size={16} className="text-red-600" /> Export PDF snapshot
                                    </button>
                                    <button onClick={() => doExport("csv")}
                                            className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2 text-slate-800"
                                            data-testid="ct-export-csv">
                                        <FileCsv size={16} className="text-emerald-600" /> Export CSV records
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Live Operation Panel — the primary flow. Sits above the retroactive form. */}
            <LiveOperationPanel
                live={live}
                canInput={canInput}
                canEnd={user && (user.role === "admin" || user.role === "tippler_operator")}
                isMillwright={user && (user.role === "shift_millwright" || user.role === "electrician" || user.role === "admin")}
                onOpenStart={() => setShowStart(true)}
                onBumpWagon={bumpWagon}
                onOpenDelay={() => setShowDelay(true)}
                onResolveDelay={resolveDelay}
                onEnd={endOperation}
                onAckBreakdown={ackBreakdown}
                onOpenFeedback={(d) => setFeedbackDelay(d)}
            />

            {/* Data-entry form — top-of-screen so Tippler operators can capture records first */}
            <HeadedCard title="Retroactive Entry · Capture completed consignment"
                        testid="ct-form-card"
                        right={
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                                {canDelete && (
                                    <>
                                        <button
                                            type="button" onClick={seedSamples}
                                            title="Insert 10 plausible sample records across the past 7 days — for demo / testing"
                                            className="px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold uppercase tracking-wider shadow-sm"
                                            data-testid="ct-seed-samples-btn"
                                        >
                                            🌱 Seed 10 Records
                                        </button>
                                        <button
                                            type="button" onClick={wipeSamples}
                                            title="Delete every record tagged as sample data — one-tap cleanup before go-live"
                                            className="px-2.5 py-1 rounded bg-rose-700 hover:bg-rose-800 text-white text-[10px] font-bold uppercase tracking-wider shadow-sm"
                                            data-testid="ct-wipe-samples-btn"
                                        >
                                            🧹 Wipe Samples
                                        </button>
                                    </>
                                )}
                                {canInput && (
                                    <button
                                        type="button"
                                        onClick={fillSample}
                                        title="Populate the form with a plausible sample record — useful for testing without live operations"
                                        className="px-2.5 py-1 rounded bg-amber-500 hover:bg-amber-600 text-slate-900 text-[10px] font-bold uppercase tracking-wider shadow-sm"
                                        data-testid="ct-form-fill-sample"
                                    >
                                        ⚡ Fill Sample Data
                                    </button>
                                )}
                                {!canInput
                                    ? <span className="text-[10px] uppercase text-amber-300 font-bold">View-only</span>
                                    : <span className="text-[10px] uppercase text-emerald-400 font-bold">All calculations auto-run on save</span>}
                            </div>
                        }>
                <fieldset disabled={!canInput || saving} className={!canInput ? "opacity-60" : ""}>
                    {/* Row 1: identity + offload window */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
                        <FormField label="Date">
                            <input type="date" value={form.date} onChange={set("date")} className="g-input" data-testid="ct-form-date" />
                        </FormField>
                        <FormField label="Train ID">
                            <input type="text" value={form.train_id} onChange={set("train_id")} className="g-input" placeholder="T001" data-testid="ct-form-train-id" />
                        </FormField>
                        <FormField label="Wagon Count">
                            <input type="number" min="1" value={form.wagon_count} onChange={set("wagon_count")} className="g-input" data-testid="ct-form-wagons" />
                        </FormField>
                        <FormField label="Shift">
                            <select value={form.shift} onChange={set("shift")} className="g-input" data-testid="ct-form-shift">
                                <option value="day">Day</option>
                                <option value="night">Night</option>
                            </select>
                        </FormField>
                        <TimeField label="Offload Start"
                            value={form.offloading_start} onChange={set("offloading_start")}
                            onNow={stampNow("offloading_start")} testid="ct-form-off-start" />
                        <TimeField label="Offload End"
                            value={form.offloading_end} onChange={set("offloading_end")}
                            onNow={stampNow("offloading_end")} testid="ct-form-off-end" />
                    </div>

                    {/* Row 2: maintenance window + equipment picker */}
                    <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="uppercase-label text-[10px] text-slate-500 mb-2">Maintenance Delay (leave blank if none)</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
                            <TimeField label="Maint Activation"
                                value={form.maintenance_activation_time} onChange={set("maintenance_activation_time")}
                                onNow={stampNow("maintenance_activation_time")} testid="ct-form-maint-act" />
                            <TimeField label="Maint Arrival"
                                value={form.maintenance_arrival_time} onChange={set("maintenance_arrival_time")}
                                onNow={stampNow("maintenance_arrival_time")} testid="ct-form-maint-arr" />
                            <TimeField label="Repair Start"
                                value={form.repair_start_time} onChange={set("repair_start_time")}
                                onNow={stampNow("repair_start_time")} testid="ct-form-rep-start" />
                            <TimeField label="Repair End"
                                value={form.repair_end_time} onChange={set("repair_end_time")}
                                onNow={stampNow("repair_end_time")} testid="ct-form-rep-end" />
                            <div className="col-span-2">
                                <FormField label="Equipment causing delay">
                                    <select value={form.equipment} onChange={set("equipment")}
                                            className="g-input" data-testid="ct-form-equipment">
                                        <option value="">— Select equipment (if applicable) —</option>
                                        {EQUIPMENT_OPTIONS.map((e) => (
                                            <option key={e} value={e}>{e}</option>
                                        ))}
                                    </select>
                                </FormField>
                            </div>
                        </div>
                    </div>

                    {/* Row 3: operational delay breakdown */}
                    <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="uppercase-label text-[10px] text-slate-500 mb-2">Operational Delay (minutes)</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-2 text-xs">
                            <FormField label="Shunting">
                                <input type="number" min="0" step="0.1" value={form.shunting_minutes} onChange={set("shunting_minutes")} className="g-input" data-testid="ct-form-shunt" />
                            </FormField>
                            <FormField label="Placement (positioning)">
                                <input type="number" min="0" step="0.1" value={form.placement_minutes} onChange={set("placement_minutes")} className="g-input" data-testid="ct-form-placement" />
                            </FormField>
                            <FormField label="Removal of empty wagons">
                                <input type="number" min="0" step="0.1" value={form.removal_minutes} onChange={set("removal_minutes")} className="g-input" data-testid="ct-form-removal" />
                            </FormField>
                            <FormField label="Other operational">
                                <input type="number" min="0" step="0.1" value={form.other_operational_minutes} onChange={set("other_operational_minutes")} className="g-input" data-testid="ct-form-other" />
                            </FormField>
                        </div>
                    </div>

                    {/* Row 3.5: additional delays (retroactive backfill) */}
                    <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="flex items-center justify-between mb-2">
                            <div className="uppercase-label text-[10px] text-slate-500">
                                Additional Delays ({(form.additional_delays || []).length})
                            </div>
                            <button
                                type="button"
                                onClick={() => setForm((f) => ({
                                    ...f,
                                    additional_delays: [...(f.additional_delays || []), { ...EMPTY_EXTRA_DELAY }],
                                }))}
                                className="px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold uppercase tracking-wider"
                                data-testid="ct-form-add-extra-delay">
                                + Add Delay
                            </button>
                        </div>
                        {(form.additional_delays || []).length === 0 && (
                            <div className="text-[11px] text-slate-400 italic">
                                Click &quot;+ Add Delay&quot; to capture historic delays that occurred during this consignment.
                            </div>
                        )}
                        {(form.additional_delays || []).map((d, idx) => {
                            const isBd = d.types.includes("breakdown");
                            const setExtra = (patch) => setForm((f) => {
                                const arr = [...(f.additional_delays || [])];
                                arr[idx] = { ...arr[idx], ...patch };
                                return { ...f, additional_delays: arr };
                            });
                            const removeExtra = () => setForm((f) => ({
                                ...f,
                                additional_delays: (f.additional_delays || []).filter((_, i) => i !== idx),
                            }));
                            const toggleType = (t) => setExtra({
                                types: d.types.includes(t) ? d.types.filter((x) => x !== t) : [...d.types, t],
                            });
                            return (
                                <div key={idx} className="mb-2 p-2 border border-slate-200 rounded bg-slate-50"
                                     data-testid={`ct-extra-delay-${idx}`}>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[10px] font-bold text-slate-700 uppercase">Delay #{idx + 1}</span>
                                        <button type="button" onClick={removeExtra}
                                                className="text-red-600 text-[10px] font-bold hover:text-red-800"
                                                data-testid={`ct-extra-delay-remove-${idx}`}>
                                            ✕ Remove
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-1 mb-2">
                                        {Object.entries(DELAY_TYPE_LABELS).map(([key, meta]) => {
                                            const active = d.types.includes(key);
                                            return (
                                                <button key={key} type="button"
                                                    onClick={() => toggleType(key)}
                                                    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${active ? "text-white" : "bg-white text-slate-700 border-slate-300"}`}
                                                    style={active ? { background: meta.color, borderColor: meta.color } : {}}
                                                    data-testid={`ct-extra-type-${idx}-${key}`}>
                                                    {active ? "✓ " : ""}{meta.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                        <FormField label="Reason">
                                            <input type="text" value={d.reason}
                                                   onChange={(e) => setExtra({ reason: e.target.value })}
                                                   className="g-input" data-testid={`ct-extra-reason-${idx}`} />
                                        </FormField>
                                        {isBd && (
                                            <FormField label="Equipment">
                                                <select value={d.equipment}
                                                        onChange={(e) => setExtra({ equipment: e.target.value })}
                                                        className="g-input"
                                                        data-testid={`ct-extra-equipment-${idx}`}>
                                                    <option value="">— Select —</option>
                                                    {EQUIPMENT_OPTIONS.map((eq) => <option key={eq} value={eq}>{eq}</option>)}
                                                </select>
                                            </FormField>
                                        )}
                                        <FormField label="Start">
                                            <input type="time" step="1" value={d.start_time}
                                                   onChange={(e) => setExtra({ start_time: e.target.value })}
                                                   className="g-input" data-testid={`ct-extra-start-${idx}`} />
                                        </FormField>
                                        <FormField label="End">
                                            <input type="time" step="1" value={d.end_time}
                                                   onChange={(e) => setExtra({ end_time: e.target.value })}
                                                   className="g-input" data-testid={`ct-extra-end-${idx}`} />
                                        </FormField>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Row 4: remarks + save/clear */}
                    <div className="mt-3 pt-3 border-t border-slate-200 flex flex-col md:flex-row gap-3 items-end">
                        <div className="flex-1">
                            <label className="uppercase-label block text-[10px] mb-1">Remarks / Comments</label>
                            <textarea value={form.remarks} onChange={set("remarks")} rows={2} className="g-input" data-testid="ct-form-remarks"
                                      placeholder="Free-text notes for this train" />
                        </div>
                        <div className="flex gap-2">
                            <button onClick={submit} disabled={!canInput || saving}
                                    className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
                                    data-testid="ct-form-save">
                                <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save Record"}
                            </button>
                            <button onClick={clear} disabled={saving}
                                    className="px-4 py-2 rounded bg-slate-500 hover:bg-slate-600 text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
                                    data-testid="ct-form-clear">
                                <ArrowClockwise size={16} weight="bold" /> Clear
                            </button>
                        </div>
                    </div>
                    {!canInput && (
                        <div className="mt-2 text-[11px] text-slate-500">
                            Only Tippler Operators, Planners (Manager role) and Admin can save records. You can still view the dashboard, exports and Six-Sigma analysis.
                        </div>
                    )}
                </fieldset>
            </HeadedCard>

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ct-kpi-row">
                <KpiCard title="Availability (Actual)" value={pct(kpi.availability_pct)} subtitle="Target ≥ 95%"
                         tone={(kpi.availability_pct || 0) >= 95 ? "green" : "red"} icon={<Gauge size={20} />} />
                <KpiCard title="Target (4 Hour Offload)" value="04:00:00" subtitle="per wagon" tone="amber"
                         icon={<Clock size={20} />} />
                <KpiCard title="Avg Cycle Time per Wagon" value={hms(kpi.avg_cycle_seconds)}
                         subtitle={`Target met ${pct(kpi.target_met_pct, 0)}`} tone="blue" icon={<Clock size={20} />} />
                <KpiCard title="Avg Total Delay" value={hms(kpi.avg_total_delay_seconds)}
                         subtitle={`${pct(kpi.delay_share_pct, 1)} of total time`} tone="red" icon={<Clock size={20} />} />
                <KpiCard title="Avg Maintenance Response" value={hms(kpi.avg_maint_response_seconds)}
                         subtitle="Target ≤ 00:30:00" tone="navy" icon={<Wrench size={20} />} />
                <KpiCard title="Total Wagons Processed" value={String(kpi.total_wagons || 0)}
                         subtitle={`${kpi.total_records || 0} trains`} tone="slate" icon={<Train size={20} />} />
            </div>

            {/* Charts row 1: Time breakdown | Delay donut | Inputs */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <HeadedCard title="Time Breakdown per Wagon (hh:mm:ss)" testid="ct-time-card">
                    <TimeBreakdownBar segments={data?.time_breakdown || []} />
                </HeadedCard>
                <HeadedCard title="Delay Breakdown (Total time)" testid="ct-delay-donut-card">
                    <div className="grid grid-cols-2 gap-2 items-center">
                        <DonutChart data={donutDelay} testid="ct-delay-donut" />
                        <div className="text-xs space-y-1.5">
                            {donutDelay.map((d) => (
                                <div key={d.label} className="flex items-center gap-2">
                                    <span className="inline-block w-3 h-3 rounded-sm" style={{ background: d.color }} />
                                    <span className="flex-1 text-slate-700">{d.label}</span>
                                    <span className="font-bold text-slate-800">{pct(d.value)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </HeadedCard>
                <HeadedCard title="Operational Delay Breakdown (Total time)" testid="ct-op-delay-card">
                    <div className="space-y-2">
                        {[
                            { k: "shunting",  label: "Shunting Time",              color: "#F59E0B" },
                            { k: "placement", label: "Placement Time (Positioning)", color: "#F59E0B" },
                            { k: "removal",   label: "Removal of Empty Wagons",    color: "#F59E0B" },
                            { k: "other",     label: "Other Operational Delays",   color: "#F59E0B" },
                        ].map((row) => (
                            <div key={row.k} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1">
                                <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: row.color }} />
                                    <span className="text-slate-700">{row.label}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-slate-800">{hms(opDelay[`${row.k}_seconds`])}</span>
                                    <span className="text-slate-500 text-xs">{pct(opDelay[`${row.k}_pct`])}</span>
                                </div>
                            </div>
                        ))}
                        <div className="flex items-center justify-between pt-2 border-t border-slate-300">
                            <span className="font-bold uppercase text-xs text-slate-800">Total Operational Delay</span>
                            <span className="font-mono font-bold text-slate-900">{hms(opDelay.total_seconds)}</span>
                        </div>
                    </div>
                </HeadedCard>
            </div>

            {/* Charts row 2: Delay by category | Target performance | Data entry */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <HeadedCard title="Delay by Category (Total time)" testid="ct-delay-bar-card">
                    <HorizontalBar rows={barDelay} testid="ct-delay-bar" />
                </HeadedCard>

                <HeadedCard title="Target Performance (4 Hour Offload)" testid="ct-target-card">
                    <div className="grid grid-cols-2 gap-2 items-center">
                        <DonutChart data={donutTarget} centerText={pct(kpi.target_met_pct, 0)}
                                    testid="ct-target-donut" />
                        <div className="text-xs space-y-2">
                            <div className="flex items-center gap-2">
                                <span className="inline-block w-3 h-3 rounded-sm bg-emerald-600" />
                                <span className="text-slate-700 flex-1">Met Target (≤ 4 h)</span>
                                <span className="font-bold text-slate-800">{pct(kpi.target_met_pct)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="inline-block w-3 h-3 rounded-sm bg-red-600" />
                                <span className="text-slate-700 flex-1">Missed Target (&gt; 4 h)</span>
                                <span className="font-bold text-slate-800">{pct(100 - (kpi.target_met_pct || 0))}</span>
                            </div>
                        </div>
                    </div>
                </HeadedCard>

                <HeadedCard title="Maintenance Delay by Equipment" testid="ct-equipment-card">
                    <div className="space-y-2 max-h-[420px] overflow-y-auto">
                        {(data?.equipment_breakdown || []).map((row) => (
                            <div key={row.equipment} className="border-b border-slate-100 pb-2" data-testid={`ct-eq-${row.equipment.replace(/\s+/g, "-").toLowerCase()}`}>
                                <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="font-semibold text-slate-800">{row.equipment}</span>
                                    <span className="text-slate-500 text-[10px]">{row.incidents} {row.incidents === 1 ? "event" : "events"}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="text-slate-500">Total maintenance delay</span>
                                    <span className="font-mono font-bold text-slate-900">{hms(row.delay_seconds)}</span>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full"
                                         style={{ width: `${Math.min(100, row.pct || 0)}%`,
                                                  background: row.equipment === "Unassigned" ? "#94A3B8" : "#DC2626" }} />
                                </div>
                                <div className="text-right text-[10px] text-slate-500 mt-0.5">{pct(row.pct)}</div>
                            </div>
                        ))}
                        {!(data?.equipment_breakdown || []).length && (
                            <div className="text-center py-6 text-slate-400 italic text-sm">
                                No maintenance delays recorded yet — tag an equipment on the record form to see this breakdown.
                            </div>
                        )}
                    </div>
                </HeadedCard>
            </div>

            {/* Charts row 3: Trend | Recent records */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <HeadedCard title="Trend — Availability & Cycle Time per Wagon" testid="ct-trend-card">
                    <div className="h-56">
                        <ResponsiveContainer>
                            <LineChart data={trendChart} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis dataKey="label" stroke="#64748B" fontSize={10} />
                                <YAxis yAxisId="left" domain={[0, 100]} stroke="#059669" fontSize={10}
                                       tickFormatter={(v) => `${v}%`} />
                                <YAxis yAxisId="right" orientation="right" stroke="#2563EB" fontSize={10}
                                       tickFormatter={(v) => `${v}m`} />
                                <Tooltip />
                                <ReferenceLine y={95} yAxisId="left" stroke="#DC2626" strokeDasharray="4 3"
                                               label={{ value: "Target ≥ 95%", position: "insideTopRight", fontSize: 10, fill: "#DC2626" }} />
                                <Line yAxisId="left" type="monotone" dataKey="availability" stroke="#059669"
                                      strokeWidth={2} name="Availability (%)" dot={{ r: 3 }} />
                                <Line yAxisId="right" type="monotone" dataKey="cycle_min" stroke="#2563EB"
                                      strokeWidth={2} name="Cycle time / wagon (min)" dot={{ r: 3 }} />
                                <Legend wrapperStyle={{ fontSize: 10 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </HeadedCard>

                <div className="lg:col-span-2">
                    <HeadedCard title="Recent Wagon Offloading Records" testid="ct-records-card">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid="ct-records-table">
                                <thead>
                                    <tr className="text-slate-600 uppercase tracking-wide text-[10px] border-b border-slate-200">
                                        <th className="text-left py-1 px-2">Date</th>
                                        <th className="text-left py-1 px-2">Train ID</th>
                                        <th className="text-right py-1 px-2">Wagons</th>
                                        <th className="text-left py-1 px-2">Offload Start</th>
                                        <th className="text-left py-1 px-2">Offload End</th>
                                        <th className="text-left py-1 px-2">Equipment</th>
                                        <th className="text-right py-1 px-2">Cycle / wagon</th>
                                        <th className="text-right py-1 px-2">Total Offload</th>
                                        <th className="text-right py-1 px-2">Total Delay</th>
                                        <th className="text-center py-1 px-2">Target</th>
                                        {canDelete && <th className="text-center py-1 px-2"></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data?.records || []).map((r) => (
                                        <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ct-row-${r.train_id}`}>
                                            <td className="py-1 px-2">{r.date}</td>
                                            <td className="py-1 px-2 font-semibold">{r.train_id}</td>
                                            <td className="py-1 px-2 text-right">{r.wagon_count}</td>
                                            <td className="py-1 px-2 font-mono">{r.offloading_start}</td>
                                            <td className="py-1 px-2 font-mono">{r.offloading_end}</td>
                                            <td className="py-1 px-2 text-slate-700">{r.equipment || <span className="text-slate-300">—</span>}</td>
                                            <td className="py-1 px-2 text-right font-mono">{hms(r.cycle_time_per_wagon_seconds)}</td>
                                            <td className="py-1 px-2 text-right font-mono">{hms(r.total_offload_seconds)}</td>
                                            <td className="py-1 px-2 text-right font-mono">{hms(r.total_delay_seconds)}</td>
                                            <td className="py-1 px-2 text-center">
                                                {r.target_met
                                                    ? <span className="inline-block w-5 h-5 rounded-full bg-emerald-600 text-white text-[10px] leading-5">✓</span>
                                                    : <span className="inline-block w-5 h-5 rounded-full bg-red-600 text-white text-[10px] leading-5">✗</span>}
                                            </td>
                                            {canDelete && (
                                                <td className="py-1 px-2 text-center">
                                                    <button onClick={() => remove(r.id, r.train_id)}
                                                            className="text-red-600 hover:text-red-700"
                                                            data-testid={`ct-delete-${r.train_id}`}>
                                                        <Trash size={14} />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                    {!(data?.records || []).length && (
                                        <tr>
                                            <td colSpan={canDelete ? 11 : 10} className="text-center py-6 text-slate-400 italic">
                                                No records for the selected date/shift. Add one using the form above.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </HeadedCard>
                </div>
            </div>

            {/* Footer legend */}
            <div className="g-card p-4 bg-slate-50 border border-slate-200 text-[11px] text-slate-700">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="font-bold text-slate-900 uppercase text-xs mb-1">Goal</div>
                        Reduce delays, speed up maintenance response and repair time, minimise operational
                        delays, and achieve ≥95% availability (≤4 hours offloading per wagon).
                    </div>
                    <div className="flex-1">
                        <div className="font-bold text-slate-900 uppercase text-xs mb-1">Definition of Times</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-0.5">
                            <div><span className="inline-block w-2.5 h-2.5 bg-emerald-600 rounded-sm mr-1" />Offloading Time — coal unloaded from wagons.</div>
                            <div><span className="inline-block w-2.5 h-2.5 bg-red-600 rounded-sm mr-1" />Maintenance Duration — time to complete the repair.</div>
                            <div><span className="inline-block w-2.5 h-2.5 bg-amber-500 rounded-sm mr-1" />Operational Delay — shunting, placement, removal.</div>
                            <div><span className="inline-block w-2.5 h-2.5 bg-violet-600 rounded-sm mr-1" />Repair Time — fault repair to return to service.</div>
                            <div><span className="inline-block w-2.5 h-2.5 bg-blue-600 rounded-sm mr-1" />Maintenance Response — activation to arrival on-site.</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Six-Sigma popup */}
            {showSpc && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
                     onClick={() => setShowSpc(false)}>
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl my-8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-4 py-3 bg-[#0B1A3B] text-white rounded-t-lg">
                            <div className="flex items-center gap-2">
                                <Waveform size={18} />
                                <div className="font-bold uppercase tracking-wide">Coal Tippler · Six-Sigma / SPC</div>
                            </div>
                            <button onClick={() => setShowSpc(false)}
                                    className="px-2 py-1 hover:bg-white/10 rounded" data-testid="ct-spc-close">✕</button>
                        </div>
                        <CoalTipplerSpcPanel />
                        <div className="px-4 pb-4 text-[11px] text-slate-500">
                            Same data is also available on the main <b>Analytics</b> page (op-type: <i>coal tippler</i>).
                        </div>
                    </div>
                </div>
            )}

            {/* Start consignment modal */}
            {showStart && (
                <StartConsignmentModal
                    form={startForm} setForm={setStartForm}
                    onClose={() => setShowStart(false)}
                    onStart={startConsignment}
                />
            )}

            {/* Log delay modal */}
            {showDelay && live.consignment && (
                <LogDelayModal
                    form={delayForm} setForm={setDelayForm}
                    onToggleType={toggleDelayType}
                    onClose={() => setShowDelay(false)}
                    onLog={logDelay}
                />
            )}

            {/* Breakdown feedback modal */}
            {feedbackDelay && (
                <FeedbackModal
                    delay={feedbackDelay}
                    isMillwright={user && (user.role === "shift_millwright" || user.role === "electrician" || user.role === "admin")}
                    onClose={() => setFeedbackDelay(null)}
                    onPost={async (text, kind) => {
                        await postFeedback(feedbackDelay.millwright?.notification_id, text, kind);
                        // Refresh reference to keep the modal in sync
                        const r = await api.get("/coal-tippler/consignments/active");
                        const updatedDelay = (r.data?.delays || []).find((x) => x.id === feedbackDelay.id);
                        if (updatedDelay) setFeedbackDelay(updatedDelay);
                    }}
                />
            )}
        </div>
    );
}

function FormField({ label, children }) {
    return (
        <div>
            <label className="uppercase-label block text-[10px] mb-0.5">{label}</label>
            {children}
        </div>
    );
}

/** Time input + a one-tap "Now" button that stamps the current wall-clock
 *  time into the field. Massively speeds up on-shift capture. */
function TimeField({ label, value, onChange, onNow, testid }) {
    const nowTestid = testid ? `${testid}-now` : undefined;
    return (
        <div>
            <label className="uppercase-label block text-[10px] mb-0.5">{label}</label>
            <div className="flex items-stretch gap-1">
                <input
                    type="time" step="1" value={value} onChange={onChange}
                    className="g-input flex-1" data-testid={testid}
                />
                <button
                    type="button"
                    onClick={onNow}
                    title={`Set ${label.toLowerCase()} to right now`}
                    aria-label={`Capture current time for ${label}`}
                    className="px-2 rounded bg-[#0B1A3B] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#152a5b] active:bg-[#081428] shadow-sm"
                    data-testid={nowTestid}
                >
                    Now
                </button>
            </div>
        </div>
    );
}

/** Inline SPC panel that calls the /coal-tippler/spc endpoint — reuses the
 *  same descriptive-stats / control / capability engine as the main
 *  Analytics page so numbers match SigmaXL. */
function CoalTipplerSpcPanel() {
    const [metric, setMetric] = useState("cycle_time_per_wagon_seconds");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        setLoading(true); setErr(null);
        api.get("/coal-tippler/spc", { params: { metric } })
            .then((r) => setData(r.data))
            .catch((e) => setErr(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [metric]);

    return (
        <div className="p-4">
            <div className="flex flex-wrap items-end gap-3 mb-3">
                <div>
                    <label className="uppercase-label block text-[10px] mb-1">Metric</label>
                    <select value={metric} onChange={(e) => setMetric(e.target.value)} className="g-input" data-testid="ct-spc-metric">
                        <option value="cycle_time_per_wagon_seconds">Cycle time / wagon</option>
                        <option value="total_delay_seconds">Total delay</option>
                        <option value="operational_delay_seconds">Operational delay</option>
                        <option value="maintenance_response_seconds">Maintenance response</option>
                        <option value="repair_time_seconds">Repair time</option>
                        <option value="offloading_time_seconds">Offloading time</option>
                        <option value="total_offload_seconds">Total offload window</option>
                        <option value="wagon_count">Wagons per train</option>
                    </select>
                </div>
            </div>
            {loading && <div className="text-sm text-slate-500">Running Six-Sigma analysis…</div>}
            {err && <div className="text-sm text-red-600">{err}</div>}
            {data && data.insufficient_data && (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                    {data.message || "Not enough records yet — enter at least 2 records to unlock SPC."}
                </div>
            )}
            {data && !data.insufficient_data && (
                <div className="grid md:grid-cols-3 gap-3 text-xs">
                    <StatBlock title="Sample size">{data.sample_count}</StatBlock>
                    <StatBlock title="Mean">{data.stats?.mean?.toFixed?.(2)} {data.metric_unit}</StatBlock>
                    <StatBlock title="Std. dev.">{data.stats?.stdev?.toFixed?.(2)} {data.metric_unit}</StatBlock>
                    <StatBlock title="Min · Max">{data.stats?.min?.toFixed?.(1)} · {data.stats?.max?.toFixed?.(1)}</StatBlock>
                    <StatBlock title="Cp / Cpk">
                        {data.capability?.cp != null ? data.capability.cp.toFixed(2) : "—"} · {" "}
                        {data.capability?.cpk != null ? data.capability.cpk.toFixed(2) : "—"}
                    </StatBlock>
                    <StatBlock title="Nelson violations">{data.nelson?.total_violations ?? 0}</StatBlock>
                    <StatBlock title="Anderson-Darling p-value">{data.normality?.p_value?.toFixed?.(3) ?? "—"}</StatBlock>
                    <StatBlock title="Sigma level">{data.capability?.sigma_level?.toFixed?.(2) ?? "—"}</StatBlock>
                    <StatBlock title="DPMO">{data.capability?.dpmo?.toFixed?.(0) ?? "—"}</StatBlock>
                    <div className="md:col-span-3">
                        <div className="uppercase-label text-[10px] mb-1 text-slate-600">Findings &amp; DMAIC hints</div>
                        <ul className="list-disc pl-5 space-y-1 text-slate-700">
                            {(data.findings || []).map((f, i) => (
                                // Findings have no stable id; the (title|severity|message) tuple
                                // is unique per analysis and never reordered, so it's stable as a key.
                                <li key={`${f.title || f.severity || "f"}-${i}`}><b>{f.title || f.severity?.toUpperCase()}</b> — {f.message || f.detail}</li>
                            ))}
                            {!data.findings?.length && <li className="italic text-slate-400">No significant issues detected.</li>}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatBlock({ title, children }) {
    return (
        <div className="border border-slate-200 rounded p-2 bg-slate-50">
            <div className="uppercase-label text-[10px] text-slate-500 mb-0.5">{title}</div>
            <div className="font-bold text-slate-900">{children ?? "—"}</div>
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════════════
 * LiveOperationPanel — the primary flow.
 * Shows "no active consignment" call-to-action, OR the running consignment
 * with wagon progress, status badge, delay history, and action buttons.
 * ═══════════════════════════════════════════════════════════════════════ */
const DELAY_TYPE_LABELS = {
    breakdown:   { label: "Breakdown",         color: "#DC2626" },
    operational: { label: "Operational",       color: "#F59E0B" },
    weather:     { label: "Weather",           color: "#0EA5E9" },
    safety:      { label: "Safety stoppage",   color: "#7C3AED" },
    no_wagons:   { label: "No wagons / rail",  color: "#94A3B8" },
    other:       { label: "Other",             color: "#475569" },
};

function LiveOperationPanel({
    live, canInput, canEnd, isMillwright, onOpenStart, onBumpWagon, onOpenDelay,
    onResolveDelay, onEnd, onAckBreakdown, onOpenFeedback,
}) {
    const c = live?.consignment;
    const delays = live?.delays || [];

    if (!c) {
        return (
            <div className="g-card overflow-hidden" data-testid="ct-live-panel">
                <div className="g-card-header-dark flex items-center justify-between">
                    <div className="font-display font-bold uppercase tracking-wide text-sm">Live Operation</div>
                    <span className="text-[10px] uppercase text-emerald-400 font-bold">No active consignment</span>
                </div>
                <div className="p-6 flex flex-col md:flex-row items-center gap-4">
                    <div className="flex-1 text-slate-600 text-sm">
                        Live tracking begins the moment a Grindrod Rail Coordinator releases wagons.
                        Head to the <b>Rail Consignments</b> panel above, accept the wagons, then tap
                        <b> Start Consignment</b> on that card to kick off the operation. Wagon count,
                        delays and equipment breakdowns are captured as they happen.
                    </div>
                </div>
            </div>
        );
    }

    const pct = c.wagon_count > 0
        ? Math.min(100, Math.round((c.current_wagon / c.wagon_count) * 100))
        : 0;
    const paused = c.status === "paused";
    const openDelays = delays.filter((d) => !d.end_time);
    const allTippled = c.current_wagon >= c.wagon_count;

    return (
        <div className="g-card overflow-hidden" data-testid="ct-live-panel">
            <div className={`px-4 py-3 flex items-center justify-between text-white ${paused ? "bg-red-700" : "bg-emerald-700"}`}>
                <div className="flex items-center gap-3">
                    <div className="text-2xl font-black tracking-wide">{c.train_id}</div>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${paused ? "bg-red-900" : "bg-emerald-900"}`}
                          data-testid="ct-live-status">
                        {paused ? "⚠ Paused" : "● Live"}
                    </span>
                    <span className="text-white/90 text-xs">
                        Shift: <b>{c.shift || "—"}</b> · Date: <b>{c.date}</b> · Start: <b>{c.offloading_start}</b>
                    </span>
                </div>
                <div className="text-xs text-white/80">
                    Started by <b>{c.created_by_name || "—"}</b>
                </div>
            </div>
            <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Wagon progress */}
                <div className="lg:col-span-2">
                    <div className="flex items-center justify-between mb-1">
                        <div className="text-slate-700 text-xs uppercase font-semibold tracking-wider">Wagons Tippled</div>
                        <div className="text-slate-700 text-xs">{c.current_wagon} / {c.wagon_count}</div>
                    </div>
                    <div className="h-8 bg-slate-100 rounded overflow-hidden border border-slate-200"
                         data-testid="ct-wagon-progress">
                        <div className={`h-full transition-all duration-500 ${allTippled ? "bg-emerald-500" : "bg-blue-600"}`}
                             style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-center text-2xl font-black mt-2" data-testid="ct-wagon-count">
                        {c.current_wagon} <span className="text-slate-400 text-lg">/ {c.wagon_count}</span>
                        <span className="text-slate-500 text-sm ml-2">({pct}%)</span>
                    </div>
                    {/* Bump buttons */}
                    <div className="flex justify-center gap-2 mt-2">
                        <button onClick={() => onBumpWagon(-1)}
                                disabled={!canInput || paused || c.current_wagon <= 0}
                                className="px-3 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 text-sm font-bold disabled:opacity-40"
                                data-testid="ct-wagon-minus">− 1</button>
                        <button onClick={() => onBumpWagon(1)}
                                disabled={!canInput || paused || c.current_wagon >= c.wagon_count}
                                className="px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold disabled:opacity-40"
                                data-testid="ct-wagon-plus">＋ 1 Wagon Tippled</button>
                        <button onClick={() => onBumpWagon(5)}
                                disabled={!canInput || paused || c.current_wagon >= c.wagon_count}
                                className="px-3 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 text-sm font-bold disabled:opacity-40"
                                data-testid="ct-wagon-plus5">＋ 5</button>
                    </div>
                    {paused && (
                        <div className="mt-3 p-3 rounded bg-red-50 border-2 border-red-300 flex flex-wrap items-center gap-3"
                             data-testid="ct-paused-banner">
                            <div className="flex-1 min-w-[200px] text-xs text-red-900">
                                <b>Operation paused</b>
                                {openDelays[0]?.reason ? (
                                    <> — {openDelays[0].reason}
                                        {openDelays[0].types?.length
                                            ? ` · ${openDelays[0].types.join(", ")}` : ""}</>
                                ) : " — resolve the active delay to resume wagon tracking."}
                            </div>
                            {canInput && openDelays[0] && (
                                <button onClick={() => onResolveDelay(openDelays[0].id, openDelays[0].reason || "delay")}
                                        className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-black uppercase tracking-widest shadow"
                                        data-testid="ct-resume-btn">
                                    ▶ Resume Operation
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Action buttons */}
                <div className="flex flex-col gap-2 justify-center">
                    {canInput && (
                        <button onClick={onOpenDelay}
                                className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm font-bold uppercase tracking-wider"
                                data-testid="ct-log-delay-btn">
                            ⚠ Log Delay / Breakdown
                        </button>
                    )}
                    {canEnd && allTippled && !paused && (
                        <button onClick={onEnd}
                                className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold uppercase tracking-wider"
                                data-testid="ct-end-op-btn">
                            ✓ End Operation
                        </button>
                    )}
                    {!allTippled && canEnd && (
                        <div className="text-[11px] text-slate-500 text-center italic">
                            End button unlocks once all {c.wagon_count} wagons are tippled.
                        </div>
                    )}
                </div>
            </div>

            {/* Delay history for this consignment */}
            <div className="border-t border-slate-200 px-4 py-3 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                    <div className="uppercase-label text-[10px] text-slate-600">
                        Delays this consignment ({delays.length})
                    </div>
                    {openDelays.length > 0 && (
                        <div className="text-[10px] text-red-700 font-bold">
                            {openDelays.length} ongoing
                        </div>
                    )}
                </div>
                {delays.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">No delays logged for this consignment yet.</div>
                ) : (
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-[10px] uppercase text-slate-500 tracking-wide">
                                <th className="text-left py-1">Type</th>
                                <th className="text-left py-1">Reason</th>
                                <th className="text-left py-1">Equipment</th>
                                <th className="text-left py-1">Start</th>
                                <th className="text-left py-1">End</th>
                                <th className="text-right py-1">Duration</th>
                                <th className="text-left py-1">Millwright</th>
                                <th className="text-right py-1">Response</th>
                                <th className="py-1"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {delays.map((d) => {
                                const isBd = (d.types || []).includes("breakdown");
                                const mw = d.millwright || {};
                                return (
                                <tr key={d.id} className="border-t border-slate-200"
                                    data-testid={`ct-delay-${d.id}`}>
                                    <td className="py-1">
                                        <div className="flex flex-wrap gap-1">
                                            {(d.types || []).map((t) => {
                                                const meta = DELAY_TYPE_LABELS[t] || { label: t, color: "#64748B" };
                                                return (
                                                    <span key={t} className="px-1.5 py-0.5 rounded text-[10px] text-white font-bold"
                                                          style={{ background: meta.color }}>{meta.label}</span>
                                                );
                                            })}
                                        </div>
                                    </td>
                                    <td className="py-1">{d.reason}</td>
                                    <td className="py-1">{d.equipment || <span className="text-slate-300">—</span>}</td>
                                    <td className="py-1 font-mono">{d.start_time}</td>
                                    <td className="py-1 font-mono">{d.end_time || <span className="text-red-600 font-bold">ONGOING</span>}</td>
                                    <td className="py-1 text-right font-mono">
                                        {d.duration_seconds != null
                                            ? `${Math.floor(d.duration_seconds/60)}m ${d.duration_seconds%60}s`
                                            : "—"}
                                    </td>
                                    <td className="py-1 text-[11px]">
                                        {!isBd
                                            ? <span className="text-slate-300">n/a</span>
                                            : !mw.acknowledged
                                                ? <span className="text-red-600 font-bold">⚠ Awaiting</span>
                                                : (
                                                    <span className="text-emerald-700">
                                                        ✓ {mw.acknowledged_by_name || "Millwright"}
                                                        <span className="text-slate-500"> @ {mw.acknowledged_at_hms}</span>
                                                    </span>
                                                )}
                                    </td>
                                    <td className="py-1 text-right font-mono text-[11px]">
                                        {!isBd || d.response_time_seconds == null
                                            ? "—"
                                            : `${Math.floor(d.response_time_seconds/60)}m ${d.response_time_seconds%60}s`}
                                    </td>
                                    <td className="py-1 text-right">
                                        <div className="flex justify-end gap-1">
                                            {isBd && (
                                                <>
                                                    {!mw.acknowledged && isMillwright && (
                                                        <button onClick={() => onAckBreakdown(d)}
                                                                className="px-2 py-0.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold"
                                                                data-testid={`ct-ack-${d.id}`}>
                                                            Ack
                                                        </button>
                                                    )}
                                                    <button onClick={() => onOpenFeedback(d)}
                                                            className="px-2 py-0.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 text-[10px] font-bold"
                                                            data-testid={`ct-feedback-${d.id}`}>
                                                        💬 {(mw.feedback || []).length || "Note"}
                                                    </button>
                                                </>
                                            )}
                                            {!d.end_time && canInput && (
                                                <button onClick={() => onResolveDelay(d.id, d.reason)}
                                                        className="px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold"
                                                        data-testid={`ct-resolve-${d.id}`}>
                                                    Resolve
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );})}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function ModalShell({ title, onClose, children, testid }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
             onClick={onClose} data-testid={testid}>
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-4 py-3 bg-[#0B1A3B] text-white rounded-t-lg">
                    <div className="font-bold uppercase tracking-wide">{title}</div>
                    <button onClick={onClose} className="px-2 py-1 hover:bg-white/10 rounded">✕</button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
}

function StartConsignmentModal({ form, setForm, onClose, onStart }) {
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    return (
        <ModalShell title="Start New Consignment" onClose={onClose} testid="ct-start-modal">
            <div className="grid grid-cols-2 gap-3">
                <FormField label="Train ID">
                    <input type="text" value={form.train_id} onChange={set("train_id")}
                           placeholder="e.g. TX-2411" className="g-input"
                           data-testid="ct-start-train-id" autoFocus />
                </FormField>
                <FormField label="Wagon Count">
                    <input type="number" min="1" value={form.wagon_count} onChange={set("wagon_count")}
                           className="g-input" data-testid="ct-start-wagons" />
                </FormField>
                <FormField label="Shift">
                    <select value={form.shift} onChange={set("shift")} className="g-input"
                            data-testid="ct-start-shift">
                        <option value="day">Day</option>
                        <option value="night">Night</option>
                    </select>
                </FormField>
                <FormField label="Start time">
                    <input type="text" value="Now (auto)" disabled className="g-input opacity-60" />
                </FormField>
            </div>
            <div className="mt-3">
                <FormField label="Remarks (optional)">
                    <textarea rows={2} value={form.remarks} onChange={set("remarks")}
                              className="g-input" placeholder="Any notes for this consignment"
                              data-testid="ct-start-remarks" />
                </FormField>
            </div>
            <div className="mt-4 flex justify-end gap-2">
                <button onClick={onClose} className="px-4 py-2 rounded bg-slate-200 hover:bg-slate-300 text-sm font-bold">Cancel</button>
                <button onClick={onStart}
                        className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold"
                        data-testid="ct-start-confirm">
                    ▶ Start Consignment
                </button>
            </div>
        </ModalShell>
    );
}

function LogDelayModal({ form, setForm, onToggleType, onClose, onLog }) {
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const isBreakdown = form.types.includes("breakdown");
    const isOperational = form.types.includes("operational");
    return (
        <ModalShell title="Log Delay / Breakdown" onClose={onClose} testid="ct-delay-modal">
            <div>
                <label className="uppercase-label block text-[10px] mb-2">Delay type (pick one or more)</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(DELAY_TYPE_LABELS).map(([key, meta]) => {
                        const active = form.types.includes(key);
                        return (
                            <button key={key} type="button"
                                onClick={() => onToggleType(key)}
                                className={`px-3 py-2 rounded text-xs font-bold uppercase tracking-wide border-2 transition
                                    ${active ? "text-white shadow" : "text-slate-700 bg-white border-slate-200 hover:border-slate-400"}`}
                                style={active ? { background: meta.color, borderColor: meta.color } : {}}
                                data-testid={`ct-delay-type-${key}`}>
                                {active ? "✓ " : ""}{meta.label}
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className="mt-3">
                <FormField label="Reason">
                    <input type="text" value={form.reason} onChange={set("reason")}
                           placeholder="Short description of the delay"
                           className="g-input" data-testid="ct-delay-reason" autoFocus />
                </FormField>
            </div>
            {isBreakdown && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded space-y-2">
                    <div className="text-xs font-bold uppercase text-red-800">Breakdown details — activates millwright</div>
                    <div className="grid grid-cols-2 gap-2">
                        <FormField label="Equipment (required)">
                            <select value={form.equipment} onChange={set("equipment")} className="g-input"
                                    data-testid="ct-delay-equipment">
                                <option value="">— Select equipment —</option>
                                {EQUIPMENT_OPTIONS.map((e) => (
                                    <option key={e} value={e}>{e}</option>
                                ))}
                            </select>
                        </FormField>
                        <FormField label="ETA (min)">
                            <input type="number" min="0" value={form.eta_minutes} onChange={set("eta_minutes")}
                                   className="g-input" data-testid="ct-delay-eta" />
                        </FormField>
                    </div>
                    <FormField label="Description for millwright">
                        <textarea rows={2} value={form.description} onChange={set("description")}
                                  className="g-input" data-testid="ct-delay-description"
                                  placeholder="What's the symptom, what has been tried?" />
                    </FormField>
                </div>
            )}
            {isOperational && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
                    <div className="text-xs font-bold uppercase text-amber-800 mb-2">Operational time (minutes)</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <FormField label="Shunting">
                            <input type="number" min="0" step="0.1" value={form.shunting_minutes} onChange={set("shunting_minutes")} className="g-input" data-testid="ct-delay-shunt" />
                        </FormField>
                        <FormField label="Placement">
                            <input type="number" min="0" step="0.1" value={form.placement_minutes} onChange={set("placement_minutes")} className="g-input" data-testid="ct-delay-placement" />
                        </FormField>
                        <FormField label="Removal">
                            <input type="number" min="0" step="0.1" value={form.removal_minutes} onChange={set("removal_minutes")} className="g-input" data-testid="ct-delay-removal" />
                        </FormField>
                        <FormField label="Other">
                            <input type="number" min="0" step="0.1" value={form.other_operational_minutes} onChange={set("other_operational_minutes")} className="g-input" data-testid="ct-delay-other-op" />
                        </FormField>
                    </div>
                </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
                <button onClick={onClose} className="px-4 py-2 rounded bg-slate-200 hover:bg-slate-300 text-sm font-bold">Cancel</button>
                <button onClick={onLog}
                        className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm font-bold"
                        data-testid="ct-delay-log-confirm">
                    Log Delay
                </button>
            </div>
        </ModalShell>
    );
}

/* ═══════════════════════════════════════════════════════════════════════
 * FeedbackModal — millwright breakdown feedback thread.
 * Read-only for the tippler operator + managers; millwrights get a text
 * area to post updates (Ack, Update, Repaired, Note). Powered by the
 * shared /api/millwright/breakdown-notifications/{nid}/feedback endpoint.
 * ═══════════════════════════════════════════════════════════════════════ */
function FeedbackModal({ delay, isMillwright, onClose, onPost }) {
    const [text, setText] = useState("");
    const [kind, setKind] = useState("update");
    const mw = delay?.millwright || {};
    const feedback = mw.feedback || [];

    return (
        <ModalShell title={`Breakdown Feedback · ${delay?.reason || ""}`} onClose={onClose} testid="ct-feedback-modal">
            <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                <div className="bg-slate-50 rounded p-2">
                    <div className="uppercase-label text-[10px] text-slate-500">Logged at</div>
                    <div className="font-mono font-bold">{delay?.start_time || "—"}</div>
                </div>
                <div className="bg-slate-50 rounded p-2">
                    <div className="uppercase-label text-[10px] text-slate-500">Acknowledged at</div>
                    <div className="font-mono font-bold">
                        {mw.acknowledged_at_hms || <span className="text-red-600">Not yet</span>}
                    </div>
                    {mw.acknowledged_by_name && (
                        <div className="text-[10px] text-slate-500 mt-0.5">by {mw.acknowledged_by_name}</div>
                    )}
                </div>
                <div className="bg-slate-50 rounded p-2">
                    <div className="uppercase-label text-[10px] text-slate-500">Response time</div>
                    <div className="font-mono font-bold">
                        {delay?.response_time_seconds != null
                            ? `${Math.floor(delay.response_time_seconds/60)}m ${delay.response_time_seconds%60}s`
                            : "—"}
                    </div>
                </div>
            </div>
            <div className="border rounded max-h-72 overflow-y-auto p-2 space-y-2 bg-white">
                {feedback.length === 0 && (
                    <div className="text-center py-6 text-slate-400 italic text-xs">
                        No feedback yet. {isMillwright ? "Post the first update below." : "The millwright will post updates here."}
                    </div>
                )}
                {feedback.map((f) => (
                    <div key={f.id || `${f.at}-${f.by_id}`}
                         className="border-l-4 pl-3 py-1"
                         style={{ borderColor: f.kind === "ack" ? "#2563EB" : f.kind === "repaired" ? "#059669" : "#94A3B8" }}>
                        <div className="flex items-center gap-2 text-[10px] text-slate-500 uppercase tracking-wider">
                            <span className="font-bold">{f.kind || "update"}</span>
                            <span>·</span>
                            <span>{f.by_name}</span>
                            <span>·</span>
                            <span className="font-mono">{f.at?.slice(11, 19)}</span>
                        </div>
                        <div className="text-sm text-slate-800 mt-0.5">{f.text}</div>
                    </div>
                ))}
            </div>
            {isMillwright && (
                <div className="mt-3 border-t pt-3">
                    <div className="grid grid-cols-4 gap-2 mb-2">
                        {["update", "diagnosed", "part_needed", "repaired"].map((k) => (
                            <button key={k} type="button" onClick={() => setKind(k)}
                                    className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide border-2
                                        ${kind === k ? "bg-blue-600 text-white border-blue-700"
                                                     : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                                    data-testid={`ct-fb-kind-${k}`}>
                                {k.replace("_", " ")}
                            </button>
                        ))}
                    </div>
                    <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
                              className="g-input" data-testid="ct-fb-text"
                              placeholder="What did you find? What was fixed? Root cause?" />
                    <div className="mt-2 flex justify-end gap-2">
                        <button onClick={onClose}
                                className="px-3 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-sm font-bold">Close</button>
                        <button
                            onClick={async () => {
                                if (!text.trim()) return;
                                await onPost(text, kind);
                                setText("");
                            }}
                            className="px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold"
                            data-testid="ct-fb-post">
                            Post Feedback
                        </button>
                    </div>
                </div>
            )}
        </ModalShell>
    );
}

